import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  useTheme,
  alpha,
  Stack,
  Tooltip,
  Snackbar,
  Divider,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tabs,
  Tab,
  Checkbox,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  Refresh,
  Assignment,
  Verified,
  Science,
  ExpandMore,
  ExpandLess,
  VerifiedUser,
  Opacity,
  Whatshot,
  Speed,
  Grain,
  ColorLens,
  AccessTime,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import samplesService, { Sample, SampleStatus, Priority } from '../../services/samplesService';
import apiService from '../../services/api';

// Granulométrie mesh sizes (in μm) - same as analyst dashboard
const MESH_SIZES = [10000, 8000, 5000, 3150, 2000, 1000, 800, 500, 300, 200, 125, 80, 45, 25, -25];

interface GranulometrieRow {
  size_um: number;
  mass_g: number | null;
  refus_percent: number;
  cumulative_refus: number;
  cumulative_passing: number;
}

interface PhysicoChimiqueResults {
  densite?: number | null;
  h2o?: number | null;
  paf?: number | null;
  mo?: number | null;
  ph?: number | null;
  blancheur_l?: number | null;
  blancheur_a?: number | null;
  blancheur_b?: number | null;
  granulometrie?: GranulometrieRow[];
  d80?: number | null;
  total_mass?: number | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const PhysicoChimiqueSupervisorDashboard: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  
  // State
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [selectedBatchSamples, setSelectedBatchSamples] = useState<Sample[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  const [validationDecision, setValidationDecision] = useState<'APPROVE' | 'REJECT'>('APPROVE');
  const [validationNotes, setValidationNotes] = useState('');
  const [isBatchValidation, setIsBatchValidation] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedSampleForView, setSelectedSampleForView] = useState<Sample | null>(null);
  const [hideWeightInCert, setHideWeightInCert] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    pending_validation: 0,
    validated_today: 0,
    urgent_samples: 0,
    rejected_today: 0,
  });

  // Physico-Chimique elements
  const PHYSICO_ELEMENTS = ['DENSITE', 'H2O', 'PAF', 'MO', 'PH', 'BLANCHEUR_L', 'BLANCHEUR_A', 'BLANCHEUR_B', 'GRANULO', 'D80'];

  // Check if sample has Physico-Chimique elements
  const hasPhysicoElements = (sample: Sample): boolean => {
    const elementRouting = sample.metadonnees?.element_lab_routing || {};
    const hasPhysicoRouting = Object.values(elementRouting).includes('LABORATOIRE_PHYSICO_CHIMIQUE');
    
    const selectedElements = sample.metadonnees?.selected_elements || [];
    const hasPhysicoSelected = selectedElements.some((el: string) => PHYSICO_ELEMENTS.includes(el));
    
    return hasPhysicoRouting || hasPhysicoSelected;
  };

  // Check if Physico analysis is completed but not validated
  const needsPhysicoValidation = (sample: Sample): boolean => {
    const analysisCompleted = sample.metadonnees?.physico_chimique_analysis_completed === true ||
                              sample.metadonnees?.physico_chimique_analysis_completed === 'true';
    const notValidated = sample.metadonnees?.physico_chimique_validated !== true &&
                         sample.metadonnees?.physico_chimique_validated !== 'true';
    return analysisCompleted && notValidated && hasPhysicoElements(sample);
  };

  // 🆕 Check if sample is multi-lab and get labs awaiting validation
  const getAwaitingLabs = (sample: Sample): string[] => {
    const elementRouting = sample.metadonnees?.element_lab_routing || {};
    const involvedLabs = [...new Set(Object.values(elementRouting))] as string[];
    
    // If not multi-lab or less than 2 labs, return empty
    if (involvedLabs.length < 2) return [];
    
    // Check which labs haven't validated yet
    const awaitingLabs: string[] = [];
    
    involvedLabs.forEach((lab: string) => {
      if (lab === 'LABORATOIRE_PHYSICO_CHIMIQUE') {
        // Skip Physico since we're in Physico dashboard
        return;
      }
      
      let validated = false;
      switch(lab) {
        case 'LABORATOIRE_ICP':
          validated = sample.metadonnees?.icp_validation_completed === true;
          break;
        case 'LABORATOIRE_AU':
          validated = sample.metadonnees?.au_validation_completed === true;
          break;
        case 'LABORATOIRE_SAA':
          validated = sample.metadonnees?.saa_validation_completed === true;
          break;
        case 'LABORATOIRE_VOLUMETRIE':
          validated = sample.metadonnees?.volumetrie_validation_completed === true;
          break;
      }
      
      if (!validated) {
        // Get short lab name
        const labName = lab.replace('LABORATOIRE_', '');
        awaitingLabs.push(labName);
      }
    });
    
    return awaitingLabs;
  };

  // Load samples awaiting Physico-Chimique validation
  const loadSamples = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load from multiple statuses - Physico validation is INDEPENDENT
      const response = await samplesService.getSamples(1, 2000, {
        status: [
          SampleStatus.LABORATOIRE_PHYSICO_CHIMIQUE_ANALYSE,
          SampleStatus.LABORATOIRE_ICP_PREP_CHIMIQUE,
          SampleStatus.LABORATOIRE_ICP_ANALYSE,
          SampleStatus.LABORATOIRE_SAA_PREP_CHIMIQUE,
          SampleStatus.LABORATOIRE_SAA_ANALYSE,
          SampleStatus.LABORATOIRE_AU_PREP_CHIMIQUE,
          SampleStatus.LABORATOIRE_AU_ANALYSE,
          SampleStatus.PREPARATION_MECANIQUE,
          SampleStatus.VALIDATION_SERVICE,
        ],
      });
      
      // Filter for samples needing Physico-Chimique validation
      const physicoSamples = (response.samples || []).filter(needsPhysicoValidation);
      
      setSamples(physicoSamples);
      
      // Calculate stats
      const urgentCount = physicoSamples.filter((s: Sample) => s.priorite === Priority.URGENTE).length;
      
      setStats({
        pending_validation: physicoSamples.length,
        validated_today: 0, // Would need separate query
        urgent_samples: urgentCount,
        rejected_today: 0,
      });
      
    } catch (error) {
      console.error('Error loading samples:', error);
      setSnackbar({ open: true, message: 'Erreur lors du chargement', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSamples();
  }, [loadSamples]);

  // Group samples by batch
  const groupSamplesByBatch = (samples: Sample[]) => {
    const batches: Record<string, Sample[]> = {};
    const individualSamples: Sample[] = [];

    samples.forEach(sample => {
      const batchInfo = sample.metadonnees?.batch_info;
      if (batchInfo && batchInfo.is_batch_sample && batchInfo.batch_number) {
        if (!batches[batchInfo.batch_number]) {
          batches[batchInfo.batch_number] = [];
        }
        batches[batchInfo.batch_number].push(sample);
      } else {
        individualSamples.push(sample);
      }
    });

    return { batches, individualSamples };
  };

  // Toggle batch expansion
  const toggleBatchExpansion = (batchNumber: string) => {
    const newExpanded = new Set(expandedBatches);
    if (newExpanded.has(batchNumber)) {
      newExpanded.delete(batchNumber);
    } else {
      newExpanded.add(batchNumber);
    }
    setExpandedBatches(newExpanded);
  };

  // Get batch position
  const getBatchPosition = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.sample_position || 1;
  };

  // Get total samples in batch
  const getTotalSamplesInBatch = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.total_samples || 1;
  };

  // Open validation dialog for single sample
  const handleValidateSample = (sample: Sample) => {
    setSelectedSample(sample);
    setSelectedBatchSamples([sample]);
    setSelectedSampleForView(sample);
    setIsBatchValidation(false);
    setValidationDecision('APPROVE');
    setValidationNotes('');
    setActiveTab(0);
    setDialogOpen(true);
  };

  // Open validation dialog for entire batch
  const handleValidateBatch = (batchSamples: Sample[]) => {
    setSelectedSample(batchSamples[0]);
    setSelectedBatchSamples(batchSamples);
    setSelectedSampleForView(batchSamples[0]);
    setIsBatchValidation(true);
    setValidationDecision('APPROVE');
    setValidationNotes('');
    setActiveTab(0);
    setDialogOpen(true);
  };

  // Complete validation
  const handleCompleteValidation = async () => {
    if (!validationNotes.trim()) {
      setSnackbar({ open: true, message: 'Veuillez ajouter un commentaire', severity: 'error' });
      return;
    }

    try {
      setProcessing(true);
      const samplesToValidate = isBatchValidation ? selectedBatchSamples : [selectedSample!];
      
      let successCount = 0;
      let errorCount = 0;

      // 🔧 FIX: Use backend /validate endpoint to trigger multi-lab validation check
      const token = localStorage.getItem('access_token');
      const validationPromises = samplesToValidate.map(async (sample) => {
        try {
          const response = await fetch(`/api/v1/samples/${sample.id}/validate`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              decision: validationDecision,
              notes: isBatchValidation 
                ? `${validationNotes} [Validation de lot Physico: ${selectedSample?.metadonnees?.batch_info?.batch_number}]`
                : validationNotes,
              validated_by: 'Physico Supervisor',
              batch_validation: isBatchValidation,
              ...(isBatchValidation && {
                batch_number: selectedSample?.metadonnees?.batch_info?.batch_number
              })
            })
          });

          if (response.ok) {
            return { success: true, sample };
          } else {
            let errorMessage = 'Erreur inconnue';
            if (response.status === 401) {
              errorMessage = 'Session expirée - Reconnectez-vous';
            } else if (response.status === 404) {
              errorMessage = 'Endpoint de validation non trouvé';
            } else {
              try {
                const errorData = await response.json();
                errorMessage = errorData.message || `Erreur ${response.status}`;
              } catch {
                errorMessage = `Erreur HTTP ${response.status}`;
              }
            }
            console.error(`❌ Validation failed for ${sample.sample_id}: ${errorMessage}`);
            return { success: false, sample, error: errorMessage };
          }
        } catch (err: any) {
          console.error(`❌ Error validating sample ${sample.sample_id}:`, err);
          return { success: false, sample, error: err.message || 'Erreur de connexion' };
        }
      });

      const results = await Promise.all(validationPromises);
      
      for (const result of results) {
        if (result.success) {
          successCount++;
        } else {
          errorCount++;
          console.error(`Failed to validate ${result.sample.sample_id}: ${result.error}`);
        }
      }

      if (hideWeightInCert && successCount > 0) {
        for (const sample of samplesToValidate) {
          try {
            await samplesService.updateSample(sample.id, {
              metadonnees: { ...sample.metadonnees, hide_weight_in_certificate: true, hide_physico_weight: true }
            } as any);
          } catch (e) { console.error(`Failed to set hide_weight for ${sample.sample_id}`, e); }
        }
      }

      const batchNumber = selectedSample?.metadonnees?.batch_info?.batch_number;
      
      if (errorCount === 0) {
        const action = validationDecision === 'APPROVE' ? 'validé' : 'rejeté';
        if (isBatchValidation) {
          setSnackbar({ 
            open: true, 
            message: `✅ Lot ${batchNumber} ${action} avec succès - ${successCount} échantillons`, 
            severity: 'success' 
          });
        } else {
          setSnackbar({ 
            open: true, 
            message: `✅ Échantillon ${selectedSample?.sample_id} ${action} avec succès`, 
            severity: 'success' 
          });
        }
      } else {
        setSnackbar({ 
          open: true, 
          message: `⚠️ ${successCount} réussis, ${errorCount} erreurs`, 
          severity: 'error' 
        });
      }

      setDialogOpen(false);
      setSelectedSample(null);
      setSelectedBatchSamples([]);
      setValidationNotes('');
      loadSamples();
      
    } catch (error) {
      console.error('Error validating:', error);
      setSnackbar({ open: true, message: 'Erreur lors de la validation', severity: 'error' });
    } finally {
      setProcessing(false);
    }
  };

  // Get results from sample
  const getResults = (sample: Sample): PhysicoChimiqueResults => {
    return sample.metadonnees?.physico_chimique_results || {};
  };

  // Get Physico elements for a sample
  // 🔥 FIX: Filter element_lab_routing by selected_elements (respects per-sample customization)
  const getPhysicoElements = (sample: Sample): string[] => {
    const selectedElements = sample.metadonnees?.selected_elements || [];
    const elementRouting = sample.metadonnees?.element_lab_routing || {};
    const isMultiLab = sample.metadonnees?.multi_lab_workflow === true || 
                       Object.keys(elementRouting).length > 0;
    
    // For multi-lab samples: filter selected_elements by routing to Physico
    if (isMultiLab && selectedElements.length > 0 && Object.keys(elementRouting).length > 0) {
      const physicoElements = selectedElements.filter((element: string) => 
        elementRouting[element] === 'LABORATOIRE_PHYSICO_CHIMIQUE'
      );
      
      if (physicoElements.length > 0) {
        console.log(`🔍 Physico Supervisor: Elements for ${sample.sample_id} (filtered by selected_elements):`, physicoElements);
        return physicoElements;
      }
    }
    
    // Fallback: Check selected_elements for Physico-Chimique elements
    if (selectedElements.length > 0) {
      return selectedElements.filter((el: string) => PHYSICO_ELEMENTS.includes(el));
    }
    
    return [];
  };

  // Format result value
  const formatValue = (value: number | null | undefined, decimals: number = 2): string => {
    if (value === null || value === undefined) return '-';
    return value.toFixed(decimals);
  };

  // Get priority color
  const getPriorityColor = (priority: Priority): 'error' | 'warning' | 'default' => {
    switch (priority) {
      case Priority.URGENTE: return 'error';
      case Priority.ELEVEE: return 'warning';
      default: return 'default';
    }
  };

  // Check which element categories are present
  const hasElement = (elements: string[], ...checks: string[]): boolean => {
    return checks.some(c => elements.includes(c));
  };

  const { batches, individualSamples } = groupSamplesByBatch(samples);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Box textAlign="center">
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>Chargement...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" color="primary">
            ✅ Validation Physico-Chimique
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Validation des résultats d'analyses physico-chimiques
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadSamples}
          disabled={loading}
        >
          Actualiser
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.light, 0.1)} 100%)` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Assignment sx={{ fontSize: 40, color: 'warning.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">{stats.pending_validation}</Typography>
                  <Typography variant="body2" color="text.secondary">En attente</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.1)} 100%)` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">{Object.keys(batches).length}</Typography>
                  <Typography variant="body2" color="text.secondary">Lots</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.light, 0.1)} 100%)` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Science sx={{ fontSize: 40, color: 'error.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">{stats.urgent_samples}</Typography>
                  <Typography variant="body2" color="text.secondary">Urgents</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.light, 0.1)} 100%)` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Verified sx={{ fontSize: 40, color: 'info.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">{individualSamples.length}</Typography>
                  <Typography variant="body2" color="text.secondary">Individuels</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Samples Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Verified color="primary" />
            Échantillons à valider
          </Typography>
          
          {samples.length === 0 ? (
            <Alert severity="info">Aucun échantillon Physico-Chimique en attente de validation</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>N° Échantillon / Lot</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Éléments</TableCell>
                    <TableCell>Priorité</TableCell>
                    <TableCell>Date analyse</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Render Batch Samples */}
                  {Object.entries(batches).map(([batchNumber, batchSamples]) => {
                    const isExpanded = expandedBatches.has(batchNumber);
                    const elements = getPhysicoElements(batchSamples[0]);
                    const awaitingLabs = getAwaitingLabs(batchSamples[0]);
                    const isMultiLab = awaitingLabs.length > 0;

                    return (
                      <React.Fragment key={batchNumber}>
                        {/* Batch Header Row */}
                        <TableRow 
                          sx={{ 
                            backgroundColor: alpha(theme.palette.success.main, 0.1),
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.2) }
                          }}
                          onClick={() => toggleBatchExpansion(batchNumber)}
                        >
                          <TableCell colSpan={6}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                              <Box display="flex" alignItems="center" gap={2}>
                                <IconButton size="small">
                                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                                <Chip 
                                  label="LOT PHYSICO-CHIMIQUE" 
                                  color="success" 
                                  variant="filled"
                                  sx={{ fontWeight: 'bold' }}
                                />
                                <Typography variant="h6" fontWeight="bold" color="success.main">
                                  {batchNumber}
                                </Typography>
                                <Chip 
                                  label={`${batchSamples.length} échantillons`} 
                                  size="small" 
                                  color="success" 
                                  variant="outlined"
                                />
                                {elements.length > 0 && (
                                  <Box display="flex" gap={0.5}>
                                    {elements.slice(0, 4).map((el) => (
                                      <Chip key={el} label={el} size="small" sx={{ fontSize: '0.7rem' }} />
                                    ))}
                                    {elements.length > 4 && (
                                      <Chip label={`+${elements.length - 4}`} size="small" />
                                    )}
                                  </Box>
                                )}
                                {isMultiLab && (
                                  <Chip 
                                    label={`⏳ En attente: ${awaitingLabs.join(', ')}`}
                                    size="small"
                                    color="warning"
                                    icon={<AccessTime />}
                                    sx={{ fontSize: '0.7rem', ml: 1 }}
                                  />
                                )}
                              </Box>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<VerifiedUser />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleValidateBatch(batchSamples);
                                }}
                                color="success"
                                sx={{ fontWeight: 'bold' }}
                              >
                                Valider le Lot
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>

                        {/* Individual Batch Sample Rows */}
                        {isExpanded && batchSamples.map((sample) => {
                          const sampleElements = getPhysicoElements(sample);
                          
                          return (
                            <TableRow 
                              key={sample.id}
                              sx={{ 
                                backgroundColor: alpha(theme.palette.success.main, 0.05),
                                '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.1) }
                              }}
                            >
                              <TableCell sx={{ pl: 6 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography fontWeight={600}>{sample.sample_id}</Typography>
                                  <Chip 
                                    label={`Sac ${getBatchPosition(sample)}/${getTotalSamplesInBatch(sample)}`}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    sx={{ fontSize: '0.65rem' }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell>{sample.client?.company_name || '-'}</TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                  {sampleElements.slice(0, 3).map((el) => (
                                    <Chip key={el} label={el} size="small" variant="outlined" />
                                  ))}
                                  {sampleElements.length > 3 && (
                                    <Chip label={`+${sampleElements.length - 3}`} size="small" />
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Chip label={sample.priorite} size="small" color={getPriorityColor(sample.priorite)} />
                              </TableCell>
                              <TableCell>
                                {sample.metadonnees?.physico_chimique_last_saved 
                                  ? new Date(sample.metadonnees.physico_chimique_last_saved).toLocaleDateString('fr-FR')
                                  : '-'}
                              </TableCell>
                              <TableCell align="center">
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<CheckCircle />}
                                  onClick={() => handleValidateSample(sample)}
                                  color="success"
                                >
                                  Individuel
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}

                  {/* Render Individual Samples */}
                  {individualSamples.map((sample) => {
                    const elements = getPhysicoElements(sample);
                    const awaitingLabs = getAwaitingLabs(sample);
                    const isMultiLab = awaitingLabs.length > 0;
                    
                    return (
                      <TableRow key={sample.id} hover>
                        <TableCell>
                          <Box>
                            <Typography fontWeight="bold">{sample.sample_id}</Typography>
                            {isMultiLab && (
                              <Chip 
                                label={`En attente: ${awaitingLabs.join(', ')}`}
                                size="small"
                                color="warning"
                                icon={<AccessTime />}
                                sx={{ mt: 0.5, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{sample.client?.company_name || '-'}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {elements.slice(0, 3).map((el) => (
                              <Chip key={el} label={el} size="small" variant="outlined" />
                            ))}
                            {elements.length > 3 && (
                              <Chip label={`+${elements.length - 3}`} size="small" />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip label={sample.priorite} size="small" color={getPriorityColor(sample.priorite)} />
                        </TableCell>
                        <TableCell>
                          {sample.metadonnees?.physico_chimique_last_saved 
                            ? new Date(sample.metadonnees.physico_chimique_last_saved).toLocaleDateString('fr-FR')
                            : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<CheckCircle />}
                            onClick={() => handleValidateSample(sample)}
                            color="success"
                          >
                            Valider
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Validation Dialog - ENHANCED with ALL elements */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ 
          background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.light, 0.1)})`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Science color="success" />
            <Typography variant="h6">
              {isBatchValidation 
                ? `Validation Lot ${selectedSample?.metadonnees?.batch_info?.batch_number} (${selectedBatchSamples.length} échantillons)`
                : `Validation - ${selectedSample?.sample_id}`
              }
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          {/* Sample selector for batch validation */}
          {isBatchValidation && selectedBatchSamples.length > 1 && (
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" gutterBottom>Sélectionner un échantillon pour voir les détails:</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {selectedBatchSamples.map((sample) => (
                  <Chip
                    key={sample.id}
                    label={sample.sample_id}
                    onClick={() => setSelectedSampleForView(sample)}
                    color={selectedSampleForView?.id === sample.id ? 'success' : 'default'}
                    variant={selectedSampleForView?.id === sample.id ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Tabs for different element categories */}
          {selectedSampleForView && (() => {
            const elements = getPhysicoElements(selectedSampleForView);
            const results = getResults(selectedSampleForView);
            
            const hasBasicElements = hasElement(elements, 'DENSITE', 'H2O', 'PAF', 'MO', 'PH');
            const hasBlancheur = hasElement(elements, 'BLANCHEUR_L', 'BLANCHEUR_A', 'BLANCHEUR_B');
            const hasGranulo = hasElement(elements, 'GRANULO', 'D80');

            // Build tabs dynamically
            const tabs: { label: string; icon: React.ReactNode }[] = [];
            if (hasBasicElements) tabs.push({ label: 'Propriétés de base', icon: <Opacity /> });
            if (hasBlancheur) tabs.push({ label: 'Blancheur', icon: <ColorLens /> });
            if (hasGranulo) tabs.push({ label: 'Granulométrie', icon: <Grain /> });

            return (
              <Box>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                  <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                    {tabs.map((tab, i) => (
                      <Tab key={i} icon={tab.icon} iconPosition="start" label={tab.label} />
                    ))}
                    <Tab label="Décision" icon={<Verified />} iconPosition="start" />
                  </Tabs>
                </Box>

                {/* Basic Properties Tab */}
                {hasBasicElements && (
                  <TabPanel value={activeTab} index={tabs.findIndex(t => t.label === 'Propriétés de base')}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        <Opacity sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Propriétés de base - {selectedSampleForView.sample_id}
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      {elements.includes('DENSITE') && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.05), border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                            <Typography variant="body2" color="text.secondary">Densité</Typography>
                            <Typography variant="h5" fontWeight="bold" color="success.main">
                              {formatValue(results.densite)} <Typography component="span" variant="body2">g/cm³</Typography>
                            </Typography>
                          </Paper>
                        </Grid>
                      )}
                      {elements.includes('H2O') && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}>
                            <Typography variant="body2" color="text.secondary">H₂O (Humidité)</Typography>
                            <Typography variant="h5" fontWeight="bold" color="info.main">
                              {formatValue(results.h2o)} <Typography component="span" variant="body2">%</Typography>
                            </Typography>
                          </Paper>
                        </Grid>
                      )}
                      {elements.includes('PAF') && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.error.main, 0.05), border: `1px solid ${alpha(theme.palette.error.main, 0.2)}` }}>
                            <Typography variant="body2" color="text.secondary">PAF (Perte au Feu)</Typography>
                            <Typography variant="h5" fontWeight="bold" color="error.main">
                              {formatValue(results.paf)} <Typography component="span" variant="body2">%</Typography>
                            </Typography>
                          </Paper>
                        </Grid>
                      )}
                      {elements.includes('MO') && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.warning.main, 0.05), border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}` }}>
                            <Typography variant="body2" color="text.secondary">MO (Matière Organique)</Typography>
                            <Typography variant="h5" fontWeight="bold" color="warning.main">
                              {formatValue(results.mo)} <Typography component="span" variant="body2">%</Typography>
                            </Typography>
                          </Paper>
                        </Grid>
                      )}
                      {elements.includes('PH') && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.secondary.main, 0.05), border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}` }}>
                            <Typography variant="body2" color="text.secondary">pH</Typography>
                            <Typography variant="h5" fontWeight="bold" color="secondary.main">
                              {formatValue(results.ph, 1)}
                            </Typography>
                          </Paper>
                        </Grid>
                      )}
                    </Grid>
                  </TabPanel>
                )}

                {/* Blancheur Tab */}
                {hasBlancheur && (
                  <TabPanel value={activeTab} index={tabs.findIndex(t => t.label === 'Blancheur')}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        <ColorLens sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Blancheur (Colorimétrie L*a*b*) - {selectedSampleForView.sample_id}
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      {elements.includes('BLANCHEUR_L') && (
                        <Grid item xs={12} sm={4}>
                          <Paper sx={{ p: 3, bgcolor: '#f5f5f5', border: '1px solid #ccc', textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>L* (Luminosité)</Typography>
                            <Typography variant="h3" fontWeight="bold">
                              {formatValue(results.blancheur_l, 1)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              0 (noir) → 100 (blanc)
                            </Typography>
                          </Paper>
                        </Grid>
                      )}
                      {elements.includes('BLANCHEUR_A') && (
                        <Grid item xs={12} sm={4}>
                          <Paper sx={{ p: 3, bgcolor: '#fff5f5', border: '1px solid #ffcccc', textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>a* (Rouge/Vert)</Typography>
                            <Typography variant="h3" fontWeight="bold" color={results.blancheur_a && results.blancheur_a > 0 ? 'error.main' : 'success.main'}>
                              {formatValue(results.blancheur_a, 1)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              -a (vert) → +a (rouge)
                            </Typography>
                          </Paper>
                        </Grid>
                      )}
                      {elements.includes('BLANCHEUR_B') && (
                        <Grid item xs={12} sm={4}>
                          <Paper sx={{ p: 3, bgcolor: '#fffff5', border: '1px solid #ffffcc', textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>b* (Jaune/Bleu)</Typography>
                            <Typography variant="h3" fontWeight="bold" color={results.blancheur_b && results.blancheur_b > 0 ? 'warning.main' : 'info.main'}>
                              {formatValue(results.blancheur_b, 1)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              -b (bleu) → +b (jaune)
                            </Typography>
                          </Paper>
                        </Grid>
                      )}
                    </Grid>
                  </TabPanel>
                )}

                {/* Granulométrie Tab */}
                {hasGranulo && (
                  <TabPanel value={activeTab} index={tabs.findIndex(t => t.label === 'Granulométrie')}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        <Grain sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Granulométrie - {selectedSampleForView.sample_id}
                      </Typography>
                    </Box>
                    
                    {/* D80 and Total Mass Summary */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), border: `2px solid ${theme.palette.primary.main}` }}>
                          <Typography variant="body2" color="text.secondary">D80 (80% passant)</Typography>
                          <Typography variant="h4" fontWeight="bold" color="primary.main">
                            {formatValue(results.d80, 0)} <Typography component="span" variant="body1">μm</Typography>
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.secondary.main, 0.1) }}>
                          <Typography variant="body2" color="text.secondary">Masse totale</Typography>
                          <Typography variant="h4" fontWeight="bold" color="secondary.main">
                            {formatValue(results.total_mass, 2)} <Typography component="span" variant="body1">g</Typography>
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Granulométrie Table */}
                    {results.granulometrie && results.granulometrie.length > 0 ? (
                      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', bgcolor: theme.palette.grey[100] }}>Tamis (μm)</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: theme.palette.grey[100] }}>Masse (g)</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: theme.palette.grey[100] }}>Refus (%)</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: theme.palette.grey[100] }}>Refus Cumulé (%)</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: theme.palette.grey[100] }}>Passant Cumulé (%)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {results.granulometrie.map((row, index) => (
                              <TableRow 
                                key={index}
                                sx={{ 
                                  bgcolor: row.cumulative_passing >= 80 && (index === 0 || results.granulometrie![index-1].cumulative_passing < 80) 
                                    ? alpha(theme.palette.primary.main, 0.1) 
                                    : 'inherit'
                                }}
                              >
                                <TableCell>
                                  {row.size_um === -25 ? '< 25 (fond)' : row.size_um.toLocaleString()}
                                </TableCell>
                                <TableCell align="right">
                                  {row.mass_g !== null ? row.mass_g.toFixed(2) : '-'}
                                </TableCell>
                                <TableCell align="right">
                                  {row.refus_percent ? row.refus_percent.toFixed(2) : '-'}
                                </TableCell>
                                <TableCell align="right">
                                  {row.cumulative_refus ? row.cumulative_refus.toFixed(2) : '-'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                  {row.cumulative_passing ? row.cumulative_passing.toFixed(2) : '-'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="warning">Aucune donnée de granulométrie disponible</Alert>
                    )}
                  </TabPanel>
                )}

                {/* Decision Tab - Always last */}
                <TabPanel value={activeTab} index={tabs.length}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      <Verified sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Décision de validation
                    </Typography>
                  </Box>

                  {/* Sample Info */}
                  <Paper sx={{ p: 2, mb: 3, bgcolor: alpha(theme.palette.grey[500], 0.05) }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Client:</strong> {selectedSampleForView?.client?.company_name}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Priorité:</strong> {selectedSampleForView?.priorite}
                        </Typography>
                      </Grid>
                      {isBatchValidation && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Échantillons du lot:</strong> {selectedBatchSamples.map(s => s.sample_id).join(', ')}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>

                  {/* Validation Decision */}
                  <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                    <RadioGroup
                      value={validationDecision}
                      onChange={(e) => setValidationDecision(e.target.value as 'APPROVE' | 'REJECT')}
                    >
                      <Paper sx={{ p: 2, mb: 1, border: validationDecision === 'APPROVE' ? `2px solid ${theme.palette.success.main}` : '1px solid #ddd' }}>
                        <FormControlLabel 
                          value="APPROVE" 
                          control={<Radio color="success" />} 
                          label={
                            <Box>
                              <Typography fontWeight="bold" color="success.main">✅ Approuver</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Résultats validés - Les données seront intégrées au certificat
                              </Typography>
                            </Box>
                          }
                        />
                      </Paper>
                      <Paper sx={{ p: 2, border: validationDecision === 'REJECT' ? `2px solid ${theme.palette.error.main}` : '1px solid #ddd' }}>
                        <FormControlLabel 
                          value="REJECT" 
                          control={<Radio color="error" />} 
                          label={
                            <Box>
                              <Typography fontWeight="bold" color="error.main">❌ Rejeter</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Renvoyer pour correction - L'analyste devra refaire l'analyse
                              </Typography>
                            </Box>
                          }
                        />
                      </Paper>
                    </RadioGroup>
                  </FormControl>

                  {/* Hide Weight Option */}
                  <Paper sx={{ p: 2, mb: 2, border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`, bgcolor: alpha(theme.palette.warning.main, 0.03) }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={hideWeightInCert}
                          onChange={(e) => setHideWeightInCert(e.target.checked)}
                          color="warning"
                        />
                      }
                      label="Masquer le poids dans le certificat final"
                    />
                  </Paper>

                  {/* Validation Notes */}
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label={validationDecision === 'APPROVE' ? 'Commentaires de validation *' : 'Raison du rejet *'}
                    value={validationNotes}
                    onChange={(e) => setValidationNotes(e.target.value)}
                    placeholder={
                      validationDecision === 'APPROVE' 
                        ? 'Résultats conformes aux spécifications. Analyses vérifiées et validées.'
                        : 'Expliquez pourquoi cette analyse est rejetée (ex: valeurs aberrantes, erreur de manipulation, etc.)'
                    }
                    required
                    error={!validationNotes.trim()}
                    helperText={!validationNotes.trim() ? 'Ce champ est obligatoire' : ''}
                  />
                </TabPanel>
              </Box>
            );
          })()}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setDialogOpen(false)} disabled={processing}>
            Annuler
          </Button>
          <Button
            variant="contained"
            color={validationDecision === 'APPROVE' ? 'success' : 'error'}
            startIcon={validationDecision === 'APPROVE' ? <CheckCircle /> : <Cancel />}
            onClick={handleCompleteValidation}
            disabled={processing || !validationNotes.trim()}
            size="large"
          >
            {processing ? <CircularProgress size={20} /> : (
              isBatchValidation 
                ? `${validationDecision === 'APPROVE' ? 'Valider' : 'Rejeter'} le Lot (${selectedBatchSamples.length})`
                : (validationDecision === 'APPROVE' ? 'Valider' : 'Rejeter')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PhysicoChimiqueSupervisorDashboard;


